import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { RegistrationService } from '../shared/registration.service';
import { SubSink } from 'subsink';
import { ActivatedRoute } from '@angular/router';
import { catchError, finalize, tap } from 'rxjs/operators';
import { DocumentRequest } from 'src/app/shared/models/document-request.model';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { DocumentExtractDataService } from 'src/app/shared/services/document-extract-data.service';

const ERROR_MESSAGE = 'There was an error, please contact us for help';
const SUCCESS_MESSAGE = 'Your document was successfully uploaded';
//const SOURCE_TEXT = 'self';
const ISO_8601 = 'YYYY-MM-DDTHH:mm:ss[Z]';
const LICENSE_DOCUMENT_TYPE = 'DriverLicense';
const PRCARD_DOCUMENT_TYPE = 'PRCard';
const PASSPORT_DOCUMENT_TYPE = 'Passport';
const CANADAIAN_VISA_DOCUMENT = 'CanadaVisa';
const US_VISA_DOCUMENT = 'USVisa';
const WORK_PERMIT_DOCUMENT = 'WorkPermit';
const FAST_CARD = 'FastCard';
const HEALTH_CARD = 'HealthCard';
const EMERGENCY_RESPONSE_TRAINING = 'EmergencyResponseTraining';
const FATIGUE_MANAGEMENT__TRAINING = 'FatigueManagementTraining';
const HAZARDOUS_MATERIALS_ENDORSEMENT = 'HazardousMaterialsEndorsement';
const HOURS_OF_SERVICE = 'HoursOfService';
const MANDATORY_ENTRY_LEVEL_TRAINING = 'MandatoryEntryLevelTraining';
const MOUNTAIN_DRIVING = 'MountainDriving';
const TRANSPORTATION_OF_DANGEROUS_GOODS = 'TransportationOfDangerousGoods';
const VEHICLE_INSPECTIONS = 'VehicleInspections';
const WINTER_DRIVING = 'WinterDriving';
const WORKPLACE_HAZARDOUS_MATERIALS_INFORMATION_SYSTEM = 'WorkplaceHazardousMaterialsInformationSystem';
const DEFENSIVE_DRIVING = 'DefensiveDriving';
const CARGO_SECUREMENT = 'CargoSecurement';
const DOCUMENT_TYPES = [
  LICENSE_DOCUMENT_TYPE,
  PRCARD_DOCUMENT_TYPE,
  PASSPORT_DOCUMENT_TYPE,
  CANADAIAN_VISA_DOCUMENT,
  US_VISA_DOCUMENT,
  WORK_PERMIT_DOCUMENT,
  FAST_CARD
];
const ISSUE_DATE_REQUIRED = [FAST_CARD];
const DOC_TYPE_HEALTH_CARD = [HEALTH_CARD];
const NO_ISSUE_DATE_DOCUMENTS = [PRCARD_DOCUMENT_TYPE];

// Document types that support AI extraction
const EXTRACTION_SUPPORTED_DOCUMENTS = [
  PASSPORT_DOCUMENT_TYPE,
  PRCARD_DOCUMENT_TYPE,
  CANADAIAN_VISA_DOCUMENT,
  US_VISA_DOCUMENT,
  WORK_PERMIT_DOCUMENT,
  FAST_CARD,
  HEALTH_CARD
];
const TRAINING_CERTIFICATE_DOCUMENTS = [
  EMERGENCY_RESPONSE_TRAINING,
  FATIGUE_MANAGEMENT__TRAINING,
  HAZARDOUS_MATERIALS_ENDORSEMENT,
  HOURS_OF_SERVICE,
  MANDATORY_ENTRY_LEVEL_TRAINING,
  MOUNTAIN_DRIVING,
  TRANSPORTATION_OF_DANGEROUS_GOODS,
  VEHICLE_INSPECTIONS,
  WINTER_DRIVING,
  WORKPLACE_HAZARDOUS_MATERIALS_INFORMATION_SYSTEM,
  DEFENSIVE_DRIVING,
  CARGO_SECUREMENT
];

export interface driverDocument {
  generationDate: string;
  validToDate: string;
  typeDisplayName: string;
  isPresent: boolean;
  type: string;
}
@Component({
    selector: 'app-additional-document-upload',
    templateUrl: './additional-document-upload.component.html',
    styleUrls: ['./additional-document-upload.component.scss'],
    standalone: false
})
export class AdditionalDocumentUploadComponent implements OnInit {
  loading: boolean;
  form: UntypedFormGroup;
  formUploadFiles = { form: null };
  fileInfo: { id: string; fileName: string; generationDate: string; validToDate: string };
  documentType: string;
  isContentReady: boolean = false;
  isExtracting: boolean = false;
  isSaving: boolean = false;
  extractionFailed: boolean = false;
  extractionWarning: string = null;
  file: any;
  title: string;
  errorMessage: string;
  successMessage: string;
  documentTypeDisplayMap: { [key: string]: string } = {
    EmergencyResponseTraining: 'Emergency Response Training',
    FatigueManagementTraining: 'Fatigue Management Training',
    HazardousMaterialsEndorsement: 'Hazardous Materials Endorsement',
    HoursOfService: 'Hours Of Service',
    MandatoryEntryLevelTraining: 'Mandatory Entry Level Training',
    MountainDriving: 'Mountain Driving',
    TransportationOfDangerousGoods: 'Transportation Of Dangerous Goods',
    VehicleInspections: 'Vehicle Inspections',
    WinterDriving: 'Winter Driving',
    WorkplaceHazardousMaterialsInformationSystem: 'Workplace Hazardous Materials Information System',
    DefensiveDriving: 'Defensive Driving',
    CargoSecurement: 'Cargo Securement'
  };

  private subs = new SubSink();
  constructor(
    private readonly location: Location,
    private readonly registrationService: RegistrationService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly activatedRoute: ActivatedRoute,
    private readonly dateTimeService: DateTimeService,
    private readonly toastService: ToastService,
    private readonly documentExtractDataService: DocumentExtractDataService
  ) {}

  ngOnInit(): void {
    
    const docType = this.activatedRoute.snapshot.params.type;
    const isNoIssueDateDoc = NO_ISSUE_DATE_DOCUMENTS.includes(docType);
    const isHealthCardDoc = DOC_TYPE_HEALTH_CARD.includes(docType);
    const isFastCardDoc = ISSUE_DATE_REQUIRED.includes(docType);
    const isTrainingDoc = TRAINING_CERTIFICATE_DOCUMENTS.includes(docType);
    this.form = this.formBuilder.group({
      generationDate: (isNoIssueDateDoc || isHealthCardDoc || isFastCardDoc || isTrainingDoc) ? [''] : ['', [Validators.required]],
      file: ['', [Validators.required]]
    });
    
    if (DOCUMENT_TYPES.includes(docType)) {
      this.form.addControl('validToDate', new UntypedFormControl('', [Validators.required]));
    }
    if (isFastCardDoc) {
      this.form.addControl('issueDate', new UntypedFormControl('', [Validators.required]));
      this.form.addControl(
        'fastCardNumber',
        new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9A-Za-z-]{1,40}$')])
      );
    }
    if (isHealthCardDoc) {
      this.form.addControl('issueDate', new UntypedFormControl(''));
      this.form.addControl('validToDate', new UntypedFormControl(''));
      this.form.addControl(
        'healthCardNumber',
        new UntypedFormControl('', [Validators.required, Validators.pattern('^[0-9A-Za-z-]{1,40}$')])
      );
    }
    if (isTrainingDoc) {
      this.form.addControl('issueDate', new UntypedFormControl('', [Validators.required]));
      this.form.addControl('trainingCertificationName', new UntypedFormControl('', [Validators.required]));
      this.form.addControl('issuingOrganization', new UntypedFormControl('', [Validators.required]));
      this.form.addControl('validToDate', new UntypedFormControl(''));
      this.form.addControl('credentialId', new UntypedFormControl(''));
      this.form.addControl('credentialUrl', new UntypedFormControl(''));
    }

    this.formUploadFiles = {
      form: this.form
    };
    
    this.title = docType
      .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
      .replace(/([a-z\d])([A-Z])/g, '$1 $2');
    this.documentType = docType;
    
    // Ensure content is ready to show
    this.isContentReady = true;
  }

 getTitle(title: string): string {
  const titleMap: { [key: string]: string } = {
    'Mandatory Entry Level Training': 'MELT',
    'Hours Of Service': 'HoS',
    'Transportation Of Dangerous Goods': 'TDG',
    'Hazardous Materials Endorsement': 'HME',
    'Emergency Response Training': 'Emergency Response',
    'Workplace Hazardous Materials Information System': 'WHMIS',
    'Fatigue Management Training': 'FMT',
    'Fast Card': 'FAST Card'
  };

  return titleMap[title] || title;
}

  backAction(): void {
    this.location.back();
  }

  onFileClear(): void {
    this.extractionWarning = null;
    this.isExtracting = false;
    this.extractionFailed = false;
  }

  uploadDocument(): void {
    const file: File = this.form.value.file;
    if (EXTRACTION_SUPPORTED_DOCUMENTS.includes(this.documentType) && file) {
      this.extractDocumentData(file);
    }
  }

  saveDocument(): void {
    this.isSaving = true;
    const model = this.prepareSaveModel(this.form.value);

    if (model === undefined) {
      this.isSaving = false;
      return;
    }

    this.registrationService
      .saveDocument(model, this.form.value.file)
      .pipe(
        tap(() => {
          this.setSuccessMessage(SUCCESS_MESSAGE);
          this.toastService.showSuccess(this.successMessage);
          this.loading = false;
          this.isSaving = false;
          this.isContentReady = false;
          this.registrationService.notifyCarriersOnDocumentUpload(model.reportType, model.extension, this.form.value.file, undefined, model.validToDate);
          setTimeout(() => {
            this.backAction();
          }, 1000);
        }),
        catchError((error: any) => {
          console.error('Document upload error:', error);
          this.setErrorMessage(ERROR_MESSAGE);
          this.toastService.showError(this.errorMessage);
          this.loading = false;
          this.isSaving = false;
          return [];
        })
      )
      .subscribe({
        next: () => {},
        error: (err) => {
          console.error('Subscription error:', err);
          this.isSaving = false;
        }
      });
  }

  private extractDocumentData(file: File): void {
    this.isExtracting = true;
    this.extractionFailed = false;
    this.subs.sink = this.documentExtractDataService
      .extractDocument(file, this.documentType)
      .pipe(
        finalize(() => {
          this.isExtracting = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.patchExtractedData(response);
        },
        error: () => {
          this.extractionFailed = true;
        }
      });
  }

  private patchExtractedData(data: any): void {
    if (!data) {
      return;
    }

    this.extractionWarning = null;

    // Parse YYYY-MM-DD as local date to avoid UTC midnight timezone shift
    const parseDate = (value: string): Date | null => {
      if (!value) return null;
      const parts = value.split('-');
      if (parts.length === 3) {
        const d = new Date(+parts[0], +parts[1] - 1, +parts[2]);
        return isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(value);
      return isNaN(d.getTime()) ? null : d;
    };

    // Issue date → generationDate (all docs except PRCard which has no issue date field)
    if (this.form.get('generationDate') && data.issue_date) {
      const d = parseDate(data.issue_date);
      if (d) this.form.get('generationDate').patchValue(d);
    }

    // Issue date → issueDate (FastCard, HealthCard use issueDate control)
    if (this.form.get('issueDate') && data.issue_date) {
      const d = parseDate(data.issue_date);
      if (d) this.form.get('issueDate').patchValue(d);
    }

    // Expiry date → validToDate
    if (this.form.get('validToDate') && data.expiry_date) {
      const d = parseDate(data.expiry_date);
      if (d) {
        const isExpired = d < new Date();
        if (isExpired) {
          this.extractionWarning = 'This document appears to be expired. Please upload a valid document.';
        } else {
          this.form.get('validToDate').patchValue(d);
        }
      }
    }

    // FastCard: patch fast_card_number
    if (this.form.get('fastCardNumber') && data.fast_card_number) {
      this.form.get('fastCardNumber').patchValue(data.fast_card_number);
    }

    // HealthCard: patch health_card_number
    if (this.form.get('healthCardNumber') && data.health_card_number) {
      this.form.get('healthCardNumber').patchValue(data.health_card_number);
    }
  }

  setSuccessMessage(message: string) {
    this.successMessage = message;
  }

  setErrorMessage(message: string) {
    this.errorMessage = message;
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  getDocumentTypeKey = (displayName: string): string | undefined =>
    Object.entries(this.documentTypeDisplayMap).find(([_, label]) => label === displayName)?.[0];

  private prepareSaveModel(formValue: any): DocumentRequest {
    const extension = formValue.file.name
      .split('.')
      .pop()
      .toLowerCase();
    const formModel = formValue;

    if (['pdf', 'jpeg', 'jpg', 'png'].find(element => element == extension) == undefined) {
      return undefined;
    }

    let SOURCE_TEXT = '-';
    if (
      [PRCARD_DOCUMENT_TYPE, CANADAIAN_VISA_DOCUMENT, WORK_PERMIT_DOCUMENT].includes(
        this.activatedRoute.snapshot.params.type
      )
    ) {
      SOURCE_TEXT = 'IRCC';
    } else if (this.activatedRoute.snapshot.params.type === LICENSE_DOCUMENT_TYPE) {
      SOURCE_TEXT = 'Licensing Authority';
    } else if (this.activatedRoute.snapshot.params.type === PASSPORT_DOCUMENT_TYPE) {
      SOURCE_TEXT = 'Govt Issued';
    } else if (this.activatedRoute.snapshot.params.type === US_VISA_DOCUMENT) {
      SOURCE_TEXT = 'Dept of State';
    } else if (this.activatedRoute.snapshot.params.type === FAST_CARD) {
      SOURCE_TEXT = 'Border authority';
    } else if (this.activatedRoute.snapshot.params.value === HEALTH_CARD) {
      SOURCE_TEXT = '-';
    }

    let model: any = {
      source: SOURCE_TEXT,
      extension: extension,
      generationDate: this.dateTimeService.formatDateTime(formModel.generationDate, ISO_8601),
      reportType: this.activatedRoute.snapshot.params.type
    };
    if (DOCUMENT_TYPES.includes(this.activatedRoute.snapshot.params.type)) {
      model['validToDate'] = this.dateTimeService.formatDateTime(formModel.validToDate, ISO_8601);
    }
    if (ISSUE_DATE_REQUIRED.includes(this.activatedRoute.snapshot.params.type)) {
      model['issueDate'] = this.dateTimeService.formatDateTime(formModel.issueDate, ISO_8601);
      model['fastCardNumber'] = formModel.fastCardNumber;
    }
    if (DOC_TYPE_HEALTH_CARD.includes(this.activatedRoute.snapshot.params.type)) {
      model['issueDate'] = this.dateTimeService.formatDateTime(formModel.issueDate, ISO_8601);
      model['validToDate'] = this.dateTimeService.formatDateTime(formModel.validToDate, ISO_8601);
      model['healthCardNumber'] = formModel.healthCardNumber;

      Object.keys(model).forEach(key => {
        const value = model[key];
        const isEmpty =
          value === '' ||
          value === null ||
          value === undefined ||
          (typeof value === 'string' && value.trim() === '') ||
          (typeof value === 'string' && value.includes('Invalid Date'));
        if (isEmpty) {
          delete model[key];
        }
      });
    }
    if (
      this.activatedRoute.snapshot.params.type === 'FastCard' ||
      this.activatedRoute.snapshot.params.type === 'HealthCard' ||
      NO_ISSUE_DATE_DOCUMENTS.includes(this.activatedRoute.snapshot.params.type)
    ) {
      delete model.generationDate;
    }
    if (TRAINING_CERTIFICATE_DOCUMENTS.includes(this.activatedRoute.snapshot.params.type)) {
      delete model.generationDate;
      model['issueDate'] = this.dateTimeService.formatDateTime(formModel.issueDate, ISO_8601);
      model['validToDate'] = this.dateTimeService.formatDateTime(formModel?.validToDate, ISO_8601);
      const displayName = formModel?.trainingCertificationName;
      model['trainingCertificationName'] = this.getDocumentTypeKey(displayName);
      model['issuingOrganization'] = formModel?.issuingOrganization;
      model['credentialId'] = formModel?.credentialId;
      model['credentialUrl'] = formModel?.credentialUrl;

      Object.keys(model).forEach(key => {
        const value = model[key];
        const isEmpty =
          value === '' ||
          value === null ||
          value === undefined ||
          (typeof value === 'string' && value.trim() === '') ||
          (typeof value === 'string' && value.includes('Invalid Date'));
        
        if (isEmpty) {
          delete model[key];
        }
      });
    }
    return model as DocumentRequest;
  }
}
